// [TODO] - Not sure if anything is using this Row Variant at the moment
import React from 'react';
import { StyleSheet } from 'react-native';
import { DocumentStatus, FileType } from '@alucio/aws-beacon-amplify/src/models';
import { DNABox, DNACard, DNAText, Iffy, useTheme, colors } from '@alucio/lux-ui';
import {
  DNAChip,
  DNACustomFieldChipList,
  DNADocumentStatusChip,
  DNADocumentThumbnail,
  DNAFileTypeChip,
  DNAPopover,
  DNARowsComponent,
  useCurrentPage,
} from 'src/components/DNA';
import { DocumentVersionORM, FolderItemORM } from 'src/types/types';
import { detectArchivedFileKeyPath } from 'src/components/SlideSelector/useThumbnailSelector';
import { useMeetingsState } from 'src/state/context/Meetings/MeetingsStateProvider.proxy';

export type DNADocumentVersionMeetingRowProps = {
  isOnline?: boolean,
}

const styles = StyleSheet.create({
  bookmarkButtonPWAWrapper: {
    marginRight: 10,
    paddingRight: 30,
    width: 22,
  },
  containerStyle: {
    backgroundColor: colors['color-white-transparent-6'],
    borderRadius: 4,
    marginVertical: 4,
  },
  titleText: {
    marginLeft: 24,
    paddingRight: 16,
  },
});

const DNADocumentFolderItemMeetingRow: DNARowsComponent<
  FolderItemORM,
  DNADocumentVersionMeetingRowProps
> = ({
  ORM: folderItem,
  onPress,
  style,
  isDesktop = true,
  isOnline,
}) => {
  const theme = useTheme()
  const { meetingORM } = useMeetingsState();
  // [TODO] - I think we need to revisit this? A Folder Item should not be limited to a DocVerORM
  /** TODO: Refactor improper use of as assertion */
  const documentVersionORM = (folderItem.relations.itemORM as DocumentVersionORM)
  const route = useCurrentPage({ exact: false })
  const { status: docStatus } = documentVersionORM.relations.documentORM.model
  const { visiblePages } = folderItem.model
  const isModified =
    visiblePages?.length &&
    documentVersionORM.meta.permissions.MSLSelectSlides &&
    documentVersionORM.model.status === 'PUBLISHED'
  const isVirtualMeeting = meetingORM?.model.type === 'VIRTUAL'
  const isWebDoc = documentVersionORM.model.type === FileType.WEB
  const isHTMLDoc = documentVersionORM.model.type === FileType.HTML
  const isMultiPageHTML = !!documentVersionORM.meta.isMultiPageHTML

  /** [TODO-ORM] - We should have a property that combines isOnline and isContentCached on the
   * DocumentVersionORM rather than checking both at the component level */
  const unavailableContent =
    (!isOnline && !documentVersionORM.meta.assets.isContentCached) ||
    (isWebDoc && (isVirtualMeeting || !isOnline)) ||
    (isHTMLDoc && isVirtualMeeting && !isMultiPageHTML)

  const isDisabledHTMLorWebDocument =
    (isWebDoc && isVirtualMeeting) ||
    (isHTMLDoc && isVirtualMeeting && !isMultiPageHTML)

  const isInteractive = documentVersionORM.meta.permissions.MSLPresent && !unavailableContent;
  const isPublisherRoute = route?.configOptions?.modules?.includes('publisher')
  const handleOnPress = () => {
    onPress?.(folderItem)
  }

  // If the publisher's selected cover page was hidden by the user
  // we use the first visible page
  // [TODO-2126] - We should be able calculate this at the ORM level instead
  let customThumbnailKey: string | undefined
  const selectedThumbnail = documentVersionORM.model.selectedThumbnail ?? 1
  if (folderItem.model.visiblePages && !folderItem.model.visiblePages.find(vp => vp === selectedThumbnail)) {
    const customPage = documentVersionORM.meta.allPages[folderItem.model.visiblePages[0] - 1]
    customThumbnailKey = detectArchivedFileKeyPath(documentVersionORM.model, customPage)
  }

  return (
    <DNACard
      appearance="flat"
      interactive={isInteractive ? 'pressable' : undefined}
      onPress={handleOnPress}
      style={[
        {
          paddingHorizontal: theme['row-padding-horizontal'],
          paddingVertical: theme['row-padding-vertical'],
        },
        style,
        styles.containerStyle,
        !isInteractive && { opacity: 0.4 },
      ]}
    >
      <DNAPopover disablePopover={!isDisabledHTMLorWebDocument} placement="top-start">
        <DNAPopover.Anchor>
          <DNABox
            testID="folder-item-row"
            alignY="center"
            spacing={isDesktop ? 'between' : 'md'}
            childFill={1}
          >
            <DNABox childFill={1} alignY="center">
              <DNADocumentThumbnail.Hover
                popover={{
                  placement: 'left-start',
                }}
                documentVersionORM={documentVersionORM}
                width={120}
                height={68}
                unavailableContent={unavailableContent}
                customThumbnailKey={customThumbnailKey}
              />
              {/* Title */}
              <DNABox childFill appearance="col" spacing="sm" style={styles.titleText}>
                <DNABox fill style={{ width: 580 }}>
                  <DNAText testID="document-item-title" b1 numberOfLines={1} colorMode="dark">
                    {folderItem.meta.title}
                  </DNAText>
                </DNABox>
                <DNABox
                  alignY="center"
                  spacing="sm"
                  fill
                  childStyle={[4, { flex: 1 }]}
                >
                  <DNABox appearance="row" spacing="sm">
                    <DNACustomFieldChipList documentVersionORM={documentVersionORM} />
                    <DNAFileTypeChip orm={documentVersionORM}/>
                    <Iffy is={isPublisherRoute}>
                      <DNABox style={{ marginHorizontal: 8 }}>
                        <DNADocumentStatusChip
                          documentStatus={documentVersionORM.relations.documentORM.model.status}
                          isScheduled={documentVersionORM.meta.schedule.publish.isScheduled}
                          isDraft={
                            documentVersionORM.relations.documentORM.meta.hasUnpublishedVersion &&
                            !documentVersionORM.meta.schedule.publish.isScheduled
                          }
                        />
                      </DNABox>
                    </Iffy>
                  </DNABox>
                  <Iffy is={docStatus === DocumentStatus.ARCHIVED}>
                    <DNADocumentStatusChip documentStatus={DocumentStatus.ARCHIVED} />
                  </Iffy>
                  <Iffy is={isModified}>
                    <DNAChip.Beacon variant="MODIFIED" />
                  </Iffy>
                  <Iffy is={unavailableContent}>
                    <DNAChip.Beacon
                      testID={`content-not-available-${folderItem.meta.title}`}
                      variant="CONTENT_UNAVAILABLE"
                    />
                  </Iffy>
                </DNABox>
              </DNABox>
            </DNABox>

          </DNABox>
        </DNAPopover.Anchor>
        <DNAPopover.Content>
          <DNAText colorMode="dark">WEB and HTML file types are not presentable in virtual meetings.</DNAText>
        </DNAPopover.Content>
      </DNAPopover>
    </DNACard>
  );
}

export default DNADocumentFolderItemMeetingRow
